import React, { type PropsWithChildren, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type { CleanupFn } from '@atlaskit/pragmatic-drag-and-drop/types';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack, xcss } from '@atlaskit/primitives';

import type { Item } from '../../../../types';
import { isItemData } from '../draggable-item-data';

const wrapperStyles = xcss({
	gap: 'space.200',
});

type DraggableItemsZoneProps = PropsWithChildren<{
	analyticsSource: string;
	defaultItems: Item[];
	setDraggedItem(): void;
	shouldUseCardV2UI?: boolean;
	updateItems(items: Item[]): void;
}>;

type Overflow = {
	overflowX: string;
	overflowY: string;
};

const isEqual =
	(base: string) =>
	(value: string): boolean =>
		base === value;
const isScroll = isEqual('scroll');
const isAuto = isEqual('auto');
const isEither = (overflow: Overflow, fn: (value: string) => boolean) =>
	fn(overflow.overflowX) || fn(overflow.overflowY);

const isElementScrollable = (element: Element): boolean => {
	const style: CSSStyleDeclaration = window.getComputedStyle(element);
	const overflow: Overflow = {
		overflowX: style.overflowX,
		overflowY: style.overflowY,
	};

	return isEither(overflow, isScroll) || isEither(overflow, isAuto);
};

const getClosestScrollableElement = (element?: Element | null): Element | null => {
	if (!element) {
		return null;
	}
	if (element === document.body || element === document.documentElement) {
		return null;
	}

	if (!isElementScrollable(element)) {
		return getClosestScrollableElement(element.parentElement);
	}
	return element;
};

export const DraggableItemsZone = ({
	analyticsSource,
	children,
	defaultItems,
	setDraggedItem,
	updateItems,
	shouldUseCardV2UI = false,
}: DraggableItemsZoneProps) => {
	const items = useRef<Item[]>(defaultItems);
	const draggableZoneRef = useRef<HTMLUListElement>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		let cleanupAutoScroll: CleanupFn | null = null;

		return combine(
			monitorForElements({
				canMonitor: ({ source }) => {
					return isItemData(source.data);
				},
				onDragStart: () => {
					if (cleanupAutoScroll) {
						return;
					}

					const closestScrollable = getClosestScrollableElement(draggableZoneRef.current);

					if (!closestScrollable) {
						return;
					}
					cleanupAutoScroll = autoScrollForElements({
						element: closestScrollable,
					});
				},
				onDrop: ({ location, source }) => {
					const target = location.current.dropTargets[0];
					if (!target) {
						return;
					}

					const sourceData = source.data;
					const targetData = target.data;
					if (!isItemData(sourceData) || !isItemData(targetData)) {
						return;
					}

					const indexOfSource = defaultItems.findIndex(
						(item) => item.cardId === sourceData.item.cardId,
					);
					const indexOfTarget = defaultItems.findIndex(
						(item) => item.cardId === targetData.item.cardId,
					);
					if (indexOfTarget < 0) {
						return;
					}

					const closestEdgeOfTarget = extractClosestEdge(targetData);

					items.current = reorderWithEdge({
						list: defaultItems,
						startIndex: indexOfSource,
						indexOfTarget,
						closestEdgeOfTarget,
						axis: 'vertical',
					});

					setDraggedItem?.();

					updateItems(items.current);

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'reordered',
							actionSubject: 'items',
							actionSubjectId: 'draggableItemsZone',
							source: analyticsSource,
						},
					}).fire();
				},
			}),
			() => cleanupAutoScroll?.(),
		);
	}, [analyticsSource, items, createAnalyticsEvent, defaultItems, updateItems, setDraggedItem]);

	return (
		<Stack
			ref={draggableZoneRef}
			{...(!shouldUseCardV2UI && { xcss: wrapperStyles })}
			testId="draggable-item-zone"
		>
			{children}
		</Stack>
	);
};
